<template>
  <div class="segmented-control" ref="segm">
    <div
      class="segmented-highlight"
      :style="highlightStyle"
      :class="{ 'segmented-highlight--hover': isHoveringSelected }"
    ></div>
    <template v-for="(opt, i) in props.options" :key="i">
      <input
        :type="'radio'"
        :id="`segmented-${i}`"
        :name="groupName"
        :value="i"
        v-model="selectedIndex"
        @change="onChange"
      />
      <label
        :for="`segmented-${i}`"
        :ref="(el: any) => setLabelRef(el, i)"
        :class="{
          'selected-segmented-label--active': i === selectedIndex,
          'selected-segmented-label--hover': isHoveringSelected && i === selectedIndex,
        }"
        @mouseenter="onLabelEnter(i)"
        @mouseleave="onLabelLeave(i)"
      >
        {{ opt.label }}
      </label>
    </template>
  </div>
</template>

<script setup lang="ts">
// Modules
import {
  HTMLAttributes,
  ComponentPublicInstance,
  onBeforeUnmount,
  onMounted,
  nextTick,
  ref,
  watch,
} from "vue";

type TSegmentedOption = {
  label: string;
  value: string;
};

const props = withDefaults(
  defineProps<{
    modelValue?: TSegmentedOption;

    options?: Array<TSegmentedOption>;
    defaultOption?: TSegmentedOption;
    customStyle?: HTMLAttributes["style"];
  }>(),
  {
    modelValue: () => {
      return { label: "Все отзывы", value: "false" };
    },
  }
);

const emit = defineEmits<{
  "update:modelValue": [option: TSegmentedOption];
}>();

const segm = ref<HTMLDivElement | null>(null);
const groupName = "segmented";
const selectedIndex = ref<number>(0);
const labelRefs = ref<HTMLElement[]>([]);
const highlightStyle = ref<Record<string, string>>({
  transform: "translateX(0)",
  width: "0px",
});
const isHoveringSelected = ref<boolean>(false);

function setLabelRef(
  el: Element | ComponentPublicInstance | null,
  index: number
) {
  if (!el) return;

  const element = el instanceof HTMLElement ? el : (el as any as ComponentPublicInstance)?.$el;
  if (element instanceof HTMLElement) {
    labelRefs.value[index] = element;
  }
}

function updateHighlight() {
  const index = selectedIndex.value >= 0 ? selectedIndex.value : 0;
  const labelEl = labelRefs.value[index];

  if (!labelEl) return;

  const left = labelEl.offsetLeft;
  const width = labelEl.offsetWidth;
  highlightStyle.value = {
    transform: `translateX(${left}px)`,
    width: `${width}px`,
  };
}

function onChange() {
  nextTick().then(updateHighlight);

  if (props.options) {
    emit("update:modelValue", props.options[selectedIndex.value]);
  }
}

function onResize() {
  updateHighlight();
}

function onLabelEnter(index: number) {
  isHoveringSelected.value = index === selectedIndex.value;
}

function onLabelLeave(index: number) {
  if (index === selectedIndex.value) {
    isHoveringSelected.value = false;
  }
}

onMounted(async () => {
  await nextTick();
  window.addEventListener("resize", onResize);
  updateHighlight();
});

onBeforeUnmount(() => {
  window.removeEventListener("resize", onResize);
});

watch(selectedIndex, () => {
  nextTick().then(updateHighlight);

  onLabelEnter(selectedIndex.value);
});
</script>

<style lang="scss">
.segmented-control {
  $border-width: 1px;

  width: 100%;

  position: relative;
  display: inline-flex;
  background: #ffffff;
  border-radius: 4px;
  overflow: hidden;
  border: $border-width solid #dcdfe6;

  input[type="radio"] {
    display: none;
  }

  label {
    padding: 4px 16px;
    background: transparent;

    text-align: center;

    width: 100%;
    white-space: nowrap;

    color: #606266;
    font-size: 14px;
    font-weight: 400;
    line-height: 22px;
    transition: background-color .2s ease, color .2s ease, border-color .2s ease;
    position: relative;
    z-index: 1;

    cursor: pointer;
  }

  // hover for non-selected options
  input:not(:checked) + label:hover {
    background: rgba(64, 158, 255, 0.12);
    color: #409eff;
  }

  .segmented-highlight {
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    background: #409eff;
    color: #ffffff;
    transition: all .2s ease, color .2s ease, border-color .2s ease;
    z-index: 0;
  }
  .segmented-highlight.segmented-highlight--hover {
    filter: brightness(0.92);
  }
}

.segmented-control input:checked + label {
  color: #ffffff;
  cursor: default;
}
.segmented-control input:checked + label.selected-segmented-label--hover {
  color: #ffffff;
}
</style>
